import { checkFeatures } from '@bybeam/platform-lib/features/check';
import { FeatureName, PaymentMethod, TaxFormType } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import Icon from '@platform-ui-common/components/Icon/index.js';
import Link from '@platform-ui-common/components/Link/index.js';
import SimpleList from '@platform-ui-common/components/SimpleList/index.js';
import { SimpleListItem } from '@platform-ui-common/components/SimpleList/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import useCustomPageTitle from '@platform-ui-common/hooks/useCustomPageTitle.js';
import useTaxForms from '@platform-ui-common/hooks/useTaxForms.js';
import { PaymentMethodDisplay } from '@platform-ui-common/utilities/payment.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import useClaim from '@platform-ui/claim/hooks/useClaim.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { ReactNode } from 'react';
import { useHistory } from 'react-router';
import { ZelleNetwork } from '../Zelle/utils.js';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { POSTHOG_FEATURE_FLAGS } from '@platform-ui/providers/posthog.js';

const bold = (body: string): ReactNode => (
  <Typography variant="body" tag="span" bold>
    {body}
  </Typography>
);

const tempSanMateoContent = {
  [PaymentMethod.PhysicalCard]: [
    'You will need to provide your mailing address and confirm your email address.',
    'The card can be used for qualifying transportation-related purchases.',
    'It may take up to 7 business days to receive your card in the mail after claiming your card.',
    <>
      The card can be used just like a regular Mastercard debit card on qualifying
      transportation-related purchases. Please visit the program website for more information on how
      to use your new card:{' '}
      <Link
        external
        to={'https://101expresslanes.org/program/equity-program'}
        target="_blank"
        rel="noopener noreferrer"
        key={'learn-more'}
      >
        https://101expresslanes.org/program/equity-program
      </Link>
      .
    </>,
  ],
  [PaymentMethod.VirtualCard]: [
    'You will need to provide your mailing address and confirm your email address.',
    'Your virtual prepaid card will be created and funded when you complete your claim.',
    'You will need to create an Akimbo Now prepaid card account in order to access your funds.',
    'The card can be used for qualifying transportation-related purchases.',
    <>
      The card can be used just like a regular Mastercard debit card on qualifying
      transportation-related purchases. Please visit the program website for more information on how
      to use your new card:{' '}
      <Link
        external
        to={'https://101expresslanes.org/program/equity-program'}
        target="_blank"
        rel="noopener noreferrer"
        key={'learn-more'}
      >
        https://101expresslanes.org/program/equity-program
      </Link>
      .
    </>,
  ],
};

function Primer(): JSX.Element {
  const { taxFormWasSubmitted } = useTaxForms();
  const program = useProgram();
  const hasNecessaryFeatures = checkFeatures(program?.features, [
    FeatureName.TaxFormsW9Collection,
    FeatureName.PaymentsClaimFunds,
  ]);
  const w9taxFormWasAlreadySubmitted = taxFormWasSubmitted(TaxFormType.W9);
  const { method, fulfillmentId, getNextPage } = useClaim();
  const history = useHistory();
  const partner = usePartner();
  const shouldShowW9TaxForm = hasNecessaryFeatures && !w9taxFormWasAlreadySubmitted;
  const nextPage = shouldShowW9TaxForm ? ClaimRoutes.W9_TAX_FORM : getNextPage(method);
  const sanMateoCopyEnabled =
    useFeatureFlagEnabled(POSTHOG_FEATURE_FLAGS.tempSanMateoClaimContent) === true;

  const primerContent: Record<
    Exclude<PaymentMethod, PaymentMethod.External>,
    {
      displayName: string;
      bullets: Array<ReactNode>;
      link?: string;
    }
  > = {
    [PaymentMethod.DirectDeposit]: {
      displayName: PaymentMethodDisplay[PaymentMethod.DirectDeposit],
      bullets: [
        'You will need your account and routing numbers.',
        'It may take 1-3 business days for funds to reach your account.',
        'Entering incorrect account and routing numbers may result in funds being lost.',
        'Use a bank account that is in your name to avoid the transfer failing or being reversed.',
      ],
    },
    [PaymentMethod.Check]: {
      displayName: PaymentMethodDisplay[PaymentMethod.Check],
      bullets: [
        'You will need to provide your mailing address so we know where to send the check.',
        'It make take 7-10 business days for the check to arrive in your mailbox.',
        'If you provide an incorrect mailing address, the check will likely get lost in the mail, resulting in your funds being lost.',
      ],
    },
    [PaymentMethod.PhysicalCard]: {
      displayName: PaymentMethodDisplay[PaymentMethod.PhysicalCard],
      bullets: sanMateoCopyEnabled
        ? tempSanMateoContent[PaymentMethod.PhysicalCard]
        : [
            'You will need to provide your mailing address and confirm your email address.',
            'You can withdraw funds from the card at an ATM.',
            'The card can be used anywhere Mastercard is accepted.',
            'It may take up to 7 business days to receive your card in the mail after claiming your card.',
          ],
    },
    [PaymentMethod.VirtualCard]: {
      displayName: PaymentMethodDisplay[PaymentMethod.VirtualCard],
      bullets: sanMateoCopyEnabled
        ? tempSanMateoContent[PaymentMethod.VirtualCard]
        : [
            'You will need to provide your mailing address and confirm your email address.',
            'Your virtual prepaid card will be created and funded when you complete your claim.',
            'You will need to create an Akimbo Now prepaid card account in order to access your funds.',
            'The card can be used anywhere Mastercard is accepted.',
          ],
    },
    [PaymentMethod.Zelle]: {
      displayName: PaymentMethodDisplay[PaymentMethod.Zelle],
      bullets: [
        <>
          Your Zelle account must be under the same email address you used to create your{' '}
          {partner.name} account, {bold(`if it isn't, you will not receive your funds`)}.
        </>,
        <>Please make sure your bank is part of the {<ZelleNetwork />}.</>,
        `If your bank doesn't participate in the Zelle network, you can still enroll through the Zelle app using a Visa or Mastercard debit card.`,
        'You will receive your funds in minutes.',
      ],
    },
  };

  const { displayName, bullets, link } = primerContent[method];
  const onClick = (): void => {
    history.push(makeRoute(nextPage, { fulfillmentId }));
  };
  const header = `You’ve selected the ${displayName.toLowerCase()} option`;
  useCustomPageTitle(header);

  return (
    <div className="w-full flex flex-col items-center text-center">
      <div className="w-3/4 flex flex-col pb-8">
        <Typography variant="h1">{header}</Typography>
      </div>
      <div className="flex flex-col items-center max-w-[320px]">
        <Typography variant="largeBody">Please keep in mind:</Typography>
        <div className="flex text-left pt-4">
          <SimpleList space>
            {bullets.map((bullet, idx) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: constant array
              <SimpleListItem key={`item-${idx}`}>{bullet}</SimpleListItem>
            ))}
          </SimpleList>
        </div>
        {link && (
          <div className="flex flex-row text-center gap-2 self-center items-center pt-6 pb-5 ">
            <Icon type="infoOutline" size="MD" />
            <Link to={link}>Learn more about {displayName.toLowerCase()}</Link>
          </div>
        )}
        <div className="pt-5">
          <Button onClick={onClick}>Save And Continue</Button>
        </div>
      </div>
    </div>
  );
}

export default Primer;
